
import React from "react";
import holidayElfPlaceholder from "@/assets/holiday-elf-placeholder.svg";
import { useSceneGenerator } from "./useSceneGenerator";
import { SceneGeneratorTheme } from "./types";
import { getThemeClasses } from "./SceneGeneratorTheme";
import SceneResults from "./SceneResults";
import { Button } from "@/components/ui/button";
import { RotateCw, Gift } from "lucide-react";

interface SceneGeneratorProps {
  theme?: SceneGeneratorTheme;
  postId?: string;
}

const SceneGenerator: React.FC<SceneGeneratorProps> = ({ 
  theme = "default", 
  postId 
}) => {
  const {
    currentScene,
    isLoading,
    hasSpun,
    spinForScene,
    resetGenerator
  } = useSceneGenerator(postId);
  
  const themeClasses = getThemeClasses(theme);

  return (
    <div className={`border rounded-lg overflow-hidden ${themeClasses.container}`}>
      <div className={`p-4 border-b ${themeClasses.header}`}>
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Gift className="h-5 w-5" />
          Holiday Elf Scene Generator
        </h2>
        <p className="text-sm mt-1">
          Spin for creative elf scene ideas to surprise and delight your kids!
        </p>
      </div>

      <div className="p-4">
        {!hasSpun ? (
          <div className="text-center py-8">
            <div className="mb-4">
              <img 
                src={holidayElfPlaceholder}
                alt="Holiday Elf" 
                className="mx-auto w-32 h-32 object-contain opacity-70"
              />
              <p className="mt-4 mb-6 text-gray-600">
                Click the button below to generate a random, fun holiday elf scene!
              </p>
            </div>
            <Button
              onClick={spinForScene}
              disabled={isLoading}
              className={`px-6 py-2 rounded-md text-md ${themeClasses.button}`}
            >
              {isLoading ? (
                <>
                  <RotateCw className="h-5 w-5 mr-2 animate-spin" />
                  Spinning...
                </>
              ) : (
                <>
                  <Gift className="h-5 w-5 mr-2" />
                  Spin for a Scene
                </>
              )}
            </Button>
          </div>
        ) : currentScene ? (
          <SceneResults 
            scene={currentScene} 
            onTryAgain={resetGenerator}
            theme={theme}
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600">No scenes available. Please try again later.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SceneGenerator;
